import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import Button from './ui/Button';
import useLandingAssets from '../hooks/useLandingAssets';
import { isUploadedFile, toEmbedUrl } from '../lib/landingAssets';
import { goToApp, IMAGES } from '../constants';

// Flatten loopai_in_action assets (which may be carousels themselves) into single slides.
function buildActionSlides(assets = []) {
  const slides = [];
  assets.forEach((a) => {
    if (a.media_type === 'carousel') {
      (a.items || []).forEach((it) => slides.push({ kind: it.type, url: it.url, title: it.caption || a.title || '', poster: a.thumbnail_url }));
    } else {
      slides.push({ kind: a.media_type, url: a.url, external_url: a.external_url, title: a.title || '', poster: a.thumbnail_url });
    }
  });
  return slides;
}

const SlideMedia = ({ slide }) => {
  if (slide.kind === 'video' && isUploadedFile(slide.url)) {
    return <video src={slide.url} poster={slide.poster} controls autoPlay muted playsInline className="w-full h-full object-contain bg-black" data-testid="demo-video" />;
  }
  if (slide.kind === 'video' || slide.kind === 'url') {
    return <iframe src={toEmbedUrl(slide.external_url || slide.url)} title={slide.title || 'LoopAI in action'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="demo-embed" />;
  }
  return <img src={slide.url} alt={slide.title || ''} className="w-full h-full object-cover" />;
};

const EmptyDemo = () => (
  <div className="relative w-full h-full">
    <img src={IMAGES.hero} alt="LoopAI demo" className="w-full h-full object-cover opacity-50" />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <Sparkles className="text-violet-300 mb-3" />
      <p className="font-display text-2xl font-bold">Demo coming soon</p>
      <p className="text-white/70 text-sm mt-1">The fastest way to see LoopAI is to try it free.</p>
    </div>
  </div>
);

const NavBtn = ({ onClick, side, children }) => (
  <button onClick={onClick} className={`absolute ${side}-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 border border-white/20 flex items-center justify-center`} data-testid={`demo-${side === 'left' ? 'prev' : 'next'}-btn`}>
    {children}
  </button>
);

const Demo = ({ open, onOpenChange }) => {
  const sections = useLandingAssets();
  const slides = useMemo(() => buildActionSlides(sections?.loopai_in_action), [sections]);
  const [active, setActive] = useState(0);

  useEffect(() => { setActive(0); }, [slides.length, open]);
  const current = slides[active];
  const step = (d) => setActive((i) => (i + d + slides.length) % slides.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-4xl bg-[#0d0a18] border-white/10 text-white p-4 sm:p-6" data-testid="demo-modal">
        <DialogTitle className="font-display text-xl font-bold">See LoopAI in action</DialogTitle>
        <DialogDescription className="text-white/60 -mt-2">{current?.title || 'From idea to finished content in about a minute.'}</DialogDescription>
        <div className="relative aspect-video rounded-xl overflow-hidden bg-black" data-testid="loopai-in-action">
          {current ? <SlideMedia key={active} slide={current} /> : <EmptyDemo />}
          {slides.length > 1 && (
            <>
              <NavBtn side="left" onClick={() => step(-1)}><ChevronLeft size={20} /></NavBtn>
              <NavBtn side="right" onClick={() => step(1)}><ChevronRight size={20} /></NavBtn>
            </>
          )}
        </div>
        <Button className="justify-self-start" onClick={goToApp} data-testid="demo-start-creating-btn">Start Creating Free <ArrowRight size={16} /></Button>
      </DialogContent>
    </Dialog>
  );
};

export default Demo;
